import { formatTimestamp, meetingStatusSchema, meetingTypeSchema } from '@sl/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { StatusChip } from '../components/StatusChip.js';
import { meetingsApi, type MeetingFilters } from '../lib/api.js';

export function MeetingsPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState<MeetingFilters>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ['meetings', filters],
    queryFn: () => meetingsApi.list(filters),
  });

  const set = (key: keyof MeetingFilters, value: string): void =>
    setFilters((f) => ({ ...f, [key]: value || undefined }));

  const dateFormat = new Intl.DateTimeFormat(i18n.language === 'ar' ? 'ar-EG' : 'en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  return (
    <>
      <h1>{t('meetings.title')}</h1>

      <div className="card">
        <div className="row">
          <div className="field" style={{ flex: '1 1 100%' }}>
            <label htmlFor="q">{t('meetings.search')}</label>
            <input id="q" value={filters.q ?? ''} onChange={(e) => set('q', e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="type">{t('meetings.filterType')}</label>
            <select id="type" value={filters.type ?? ''} onChange={(e) => set('type', e.target.value)}>
              <option value="">{t('meetings.all')}</option>
              {meetingTypeSchema.options.map((o) => (
                <option key={o} value={o}>
                  {t(`types.${o}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="status">{t('meetings.filterStatus')}</label>
            <select
              id="status"
              value={filters.status ?? ''}
              onChange={(e) => set('status', e.target.value)}
            >
              <option value="">{t('meetings.all')}</option>
              {meetingStatusSchema.options.map((o) => (
                <option key={o} value={o}>
                  {t(`statuses.${o}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="from">{t('meetings.from')}</label>
            <input
              id="from"
              type="date"
              value={filters.dateFrom ?? ''}
              onChange={(e) => set('dateFrom', e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="to">{t('meetings.to')}</label>
            <input
              id="to"
              type="date"
              value={filters.dateTo ?? ''}
              onChange={(e) => set('dateTo', e.target.value)}
            />
          </div>
        </div>
      </div>

      {isLoading && <p className="muted">{t('common.loading')}</p>}
      {error && <div className="banner error">{t('common.error')}</div>}

      {data && data.items.length === 0 && <p className="muted">{t('meetings.empty')}</p>}

      <ul className="list">
        {data?.items.map((m) => (
          <li key={m.id}>
            <div className="spread">
              <Link to={`/meetings/${m.id}`} className="title">
                {m.title}
              </Link>
              <StatusChip status={m.status} />
            </div>
            <div className="row muted" style={{ marginTop: 6 }}>
              <span className="chip">{t(`types.${m.type}`)}</span>
              <span>{dateFormat.format(new Date(m.startedAt))}</span>
              {m.durationSec ? <span>{formatTimestamp(m.durationSec)}</span> : null}
              {m.department?.name ? <span>{m.department.name}</span> : null}
              {m.clientRef ? <span>{m.clientRef}</span> : null}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}
